import ApplicationController from './application_controller';

/**
 * Reveal Controller
 *
 * Toggles visibility of content based on a checkbox or button.
 *
 * Usage with checkbox:
 *   <div data-controller="reveal" data-reveal-hidden-class="hidden">
 *     <input type="checkbox" data-reveal-target="toggle" data-action="change->reveal#toggle">
 *     <div data-reveal-target="content" class="hidden">
 *       Content shown when checked
 *     </div>
 *   </div>
 *
 * Usage with inverted logic (show when unchecked):
 *   <div data-controller="reveal" data-reveal-inverted-value="true">
 *     ...
 *   </div>
 */
export default class extends ApplicationController {
  static targets = ['toggle', 'content'];
  static values = {
    hidden: { type: Boolean, default: true },
    inverted: { type: Boolean, default: false },
  };
  static classes = ['hidden'];

  declare toggleTarget: HTMLInputElement;
  declare contentTarget: HTMLElement;
  declare contentTargets: HTMLElement[];
  declare hasToggleTarget: boolean;
  declare hasContentTarget: boolean;

  declare hiddenValue: boolean;
  declare invertedValue: boolean;
  declare hasHiddenClass: boolean;
  declare hiddenClass: string;

  connect() {
    // Sync initial state with toggle
    if (this.hasToggleTarget) {
      this.updateVisibility();
    }
  }

  toggle() {
    this.updateVisibility();
  }

  show() {
    this.hiddenValue = false;
    this.applyVisibility(true);
  }

  hide() {
    this.hiddenValue = true;
    this.applyVisibility(false);
  }

  private updateVisibility() {
    const isChecked = this.toggleTarget.checked;
    const shouldShow = this.invertedValue ? !isChecked : isChecked;
    this.applyVisibility(shouldShow);
  }

  private applyVisibility(show: boolean) {
    const hiddenClass = this.hasHiddenClass ? this.hiddenClass : 'hidden';

    this.contentTargets.forEach((content) => {
      if (show) {
        content.classList.remove(hiddenClass);
        // Enable form fields
        content.querySelectorAll('input, select, textarea').forEach((field) => {
          (field as HTMLInputElement).disabled = false;
        });
      } else {
        content.classList.add(hiddenClass);
        // Disable form fields to exclude from submission
        content.querySelectorAll('input, select, textarea').forEach((field) => {
          (field as HTMLInputElement).disabled = true;
        });
      }
    });
  }
}
